import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";

/**
 * Días de prueba restantes contados por FECHA DE CALENDARIO en España
 * (Europe/Madrid), NO por diferencia bruta de horas. Así el número es estable
 * durante todo el día y baja a MEDIANOCHE española, no a la hora concreta que
 * tenga `trialEndsAt`. (Antes, con `Math.ceil(ms / día)`, el contador saltaba a
 * esa hora — p. ej. bajaba de 3 a 2 a las 22:00 en vez de a medianoche.)
 * Cuenta el día de hoy como día de prueba (inclusivo): el último día muestra 1.
 */
export function getTrialDaysLeft(trialEndsAt: string | Date | null): number {
  if (!trialEndsAt) return 0;
  // Índice de día absoluto de una fecha en la zona de España, para restar días.
  const madridDayIndex = (d: Date): number => {
    const [y, m, day] = d
      .toLocaleDateString("en-CA", { timeZone: "Europe/Madrid" })
      .split("-")
      .map(Number);
    return Math.floor(Date.UTC(y, m - 1, day) / 86400000);
  };
  const end = madridDayIndex(new Date(trialEndsAt));
  const today = madridDayIndex(new Date());
  return Math.max(0, end - today + 1);
}

interface PlanFeatures {
  hasTableMaps: boolean;
  hasWhatsApp: boolean;
  hasEmailCampaigns: boolean;
  trialDays: number;
  maxUsers: number;
  maxLocations: number;
  maxReservations: number | null; // null = unlimited
  maxCustomers: number | null; // null = unlimited
  planName: string;
  planPrice: number;
  subscriptionStatus: string;
  trialEndsAt: string | null;
  isTrialExpired: boolean;
  isActive: boolean;
  isInTrial: boolean;
  hadPaidSubscription: boolean;
  /** Cancelación programada a fin de periodo (por cualquier vía): el plan
   *  sigue vivo hasta `currentPeriodEnd` y después cae a Free sin más cobros. */
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
}

interface UsageData {
  users: number;
  locations: number;
  totalReservations: number;
  totalCustomers: number;
}

interface UseSubscriptionReturn {
  features: PlanFeatures | null;
  usage: UsageData | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  // Feature access (considers trial)
  canAccessTableMaps: boolean;
  canAccessWhatsApp: boolean;
  canAccessEmailCampaigns: boolean;
  // Limit checks (considers trial)
  canCreateReservation: boolean;
  canCreateCustomer: boolean;
  reservationsRemaining: number | null; // null = unlimited
  customersRemaining: number | null; // null = unlimited
}

/**
 * Clave compartida de React Query.
 *
 * IMPORTANTE: este hook lo consumen a la vez `TrialBanner` y
 * `AppDiscoveryBanner` (ambos viven en el `Layout`, o sea en TODAS las páginas)
 * y ademas la página que toque. Cuando cada uno hacía su propia llamada con
 * `useState`+`useEffect`, una sola carga del dashboard disparaba 4-6 peticiones
 * identicas a `/organizations/me/subscription` — y cada una consume una plaza
 * del pool de conexiones de la BD. Con una `queryKey` compartida, React Query
 * las agrupa en UNA sola y la reparte desde cache.
 */
export const SUBSCRIPTION_QUERY_KEY = ["organization", "subscription"] as const;

/** Forma de la respuesta del backend que realmente se consume aquí. */
interface SubscriptionResponse {
  stripeSubscriptionId?: string | null;
  subscriptionCancelAtPeriodEnd?: boolean;
  subscriptionCurrentPeriodEnd?: string | null;
  /** Lo calcula el servidor mirando facturas pagadas (no se borra al caducar). */
  hadPaidSubscription?: boolean;
  currentPlan: {
    name: string;
    price: number;
    hasTableMaps?: boolean;
    hasWhatsApp?: boolean;
    hasEmailCampaigns?: boolean;
    trialDays?: number;
    maxUsers: number;
    maxLocations: number;
    maxReservations: number | null;
    maxCustomers: number | null;
  };
  subscriptionStatus: string;
  trialEndsAt: string | null;
  isInTrial?: boolean;
  usage: {
    users: number;
    locations: number;
    totalReservations?: number;
    totalCustomers?: number;
  };
}

interface SubscriptionSnapshot {
  features: PlanFeatures;
  usage: UsageData;
}

/**
 * Descarga y normaliza la suscripción. Se exporta para que quien necesite el
 * dato fuera de un componente (p. ej. `LocationContext`) pueda reutilizar la
 * MISMA entrada de cache con `queryClient.fetchQuery` en vez de repetir la
 * llamada.
 */
export async function fetchSubscriptionSnapshot(): Promise<SubscriptionSnapshot> {
  const response = await apiClient.get("/organizations/me/subscription");
  const data: SubscriptionResponse = response.data.data;
  const plan = data.currentPlan;

  // Check if trial has expired
  const trialEndsAt = data.trialEndsAt ? new Date(data.trialEndsAt) : null;
  const isTrialExpired = trialEndsAt ? trialEndsAt < new Date() : false;

  // Check if subscription is active
  const isActive =
    data.subscriptionStatus === "active" ||
    data.subscriptionStatus === "trialing";

  // Check if currently in trial (backend now sends this)
  const isInTrial = data.isInTrial ?? false;

  return {
    features: {
      hasTableMaps: plan.hasTableMaps ?? false,
      hasWhatsApp: plan.hasWhatsApp ?? false,
      hasEmailCampaigns: plan.hasEmailCampaigns ?? false,
      trialDays: plan.trialDays ?? 15,
      maxUsers: plan.maxUsers,
      maxLocations: plan.maxLocations,
      maxReservations: plan.maxReservations,
      maxCustomers: plan.maxCustomers,
      planName: plan.name,
      planPrice: plan.price,
      subscriptionStatus: data.subscriptionStatus,
      trialEndsAt: data.trialEndsAt,
      isTrialExpired,
      isActive,
      isInTrial,
      // Ex-suscriptor: llegó a pagar de verdad. Lo decide el SERVIDOR mirando
      // las facturas pagadas, porque `stripeSubscriptionId` se borra cuando la
      // suscripción termina y entonces el banner volvía a hablar de la prueba
      // gratuita (caso Botarate Beach, 23-sep-2026). El `||` es solo el
      // respaldo para backends antiguos que aún no mandan el campo.
      hadPaidSubscription:
        data.hadPaidSubscription ?? !!data.stripeSubscriptionId,
      cancelAtPeriodEnd: !!data.subscriptionCancelAtPeriodEnd,
      currentPeriodEnd: data.subscriptionCurrentPeriodEnd ?? null,
    },
    usage: {
      users: data.usage.users,
      locations: data.usage.locations,
      totalReservations: data.usage.totalReservations ?? 0,
      totalCustomers: data.usage.totalCustomers ?? 0,
    },
  };
}

export function useSubscription(): UseSubscriptionReturn {
  const queryClient = useQueryClient();

  const { data, isPending, error: queryError } = useQuery({
    queryKey: SUBSCRIPTION_QUERY_KEY,
    queryFn: fetchSubscriptionSnapshot,
  });

  const features = data?.features ?? null;
  const usage = data?.usage ?? null;
  const error = queryError ? "Error al cargar la información del plan" : null;
  const isLoading = isPending;

  /** Fuerza una recarga; los demás consumidores del hook se enteran solos. */
  const refresh = useCallback(async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY });
  }, [queryClient]);

  // Feature access - during trial, all features are available
  const canAccessTableMaps =
    features?.isInTrial || (features?.hasTableMaps ?? false);
  // WhatsApp SIEMPRE es de pago (decisión 10-sep-2026): el trial NO lo
  // desbloquea — como los add-ons de IA. Solo el flag del plan.
  const canAccessWhatsApp = features?.hasWhatsApp ?? false;
  const canAccessEmailCampaigns =
    features?.isInTrial || (features?.hasEmailCampaigns ?? false);

  // Limit checks - during trial, no limits apply
  const canCreateReservation =
    !features ||
    features.isInTrial ||
    features.maxReservations === null ||
    (usage?.totalReservations ?? 0) < features.maxReservations;

  const canCreateCustomer =
    !features ||
    features.isInTrial ||
    features.maxCustomers === null ||
    (usage?.totalCustomers ?? 0) < features.maxCustomers;

  // Calculate remaining
  const reservationsRemaining =
    !features || features.isInTrial || features.maxReservations === null
      ? null
      : Math.max(0, features.maxReservations - (usage?.totalReservations ?? 0));

  const customersRemaining =
    !features || features.isInTrial || features.maxCustomers === null
      ? null
      : Math.max(0, features.maxCustomers - (usage?.totalCustomers ?? 0));

  return {
    features,
    usage,
    isLoading,
    error,
    refresh,
    canAccessTableMaps,
    canAccessWhatsApp,
    canAccessEmailCampaigns,
    canCreateReservation,
    canCreateCustomer,
    reservationsRemaining,
    customersRemaining,
  };
}
